/**
 * Seed wells — manual / grid settings / Create with AI tabs; live preview / Apply.
 */
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { scenariosApi } from '@/api/scenarios';
import { CommitNumberField } from '@/components/Scenarios/CommitNumberField';
import { SeedManualMap } from '@/components/Scenarios/SeedManualMap';
import { SeedManualPanel } from '@/components/Scenarios/SeedManualPanel';
import { VoronoiMbMap } from '@/components/Scenarios/VoronoiMbMap';
import type { MbAutoPlaceConfig, MbPoint, MbWellsAiExistingWell } from '@/types/materialBalance';
import {
  AUTO_PLACE_DEFAULTS,
  autoPlaceWells,
  buildDefaultInjField,
  contourAreaKm2,
  estimateAutoPlaceGrid,
  finalizeAutoPlaceFromAiWells,
  MAX_AUTO_PLACE_PRODUCERS,
  pointInPoly,
  type AutoPlaceResult,
  type AutoPlaceWell,
  type ProdGridPattern,
} from '@/utils/materialBalanceAutoPlace';
import {
  buildSeedPattern,
  carveSeedCandidates,
  nextSeedWellNames,
  SEED_RATIOS,
  seedRatioKey,
  type SeedCandidate,
  type SeedTool,
  type SeedWellRow,
} from '@/utils/materialBalanceSeedManual';
import type { GeoOrigin } from '@/utils/localKmProjection';

export type AutoPlaceApplyPayload = AutoPlaceResult;

export type AutoPlaceDialogTab = 'manual' | 'grid' | 'ai';

export interface MaterialBalanceAutoPlaceDialogProps {
  open: boolean;
  onClose: () => void;
  scenarioId: string;
  layerId: string;
  layerName: string;
  contour: MbPoint[];
  /** Contour origin for basemap (local km ↔ lat/lng) */
  origin?: GeoOrigin | null;
  /** Saved Create-with-AI config for this layer; null → defaults */
  initialConfig?: MbAutoPlaceConfig | null;
  /** Last saved wells of the layer (coordinates may be missing) — Manual table seed, AI constraints */
  savedWells?: SeedWellRow[];
  /** Layer start liquid rate prefilled for new manual producers; null → left empty */
  layerQStartTd?: number | null;
  /** @deprecated use defaults from AUTO_PLACE_DEFAULTS; kept for parent compat */
  year0?: number;
  year1?: number;
  qStartTd?: number;
  hasExistingWells?: boolean;
  applying?: boolean;
  onApply: (result: AutoPlaceApplyPayload) => void | Promise<void>;
}

const EMPTY_SEED_ROWS: SeedWellRow[] = [];

const round4 = (n: number) => Math.round(n * 10000) / 10000;

function validateManualRows(rows: SeedWellRow[]): string | null {
  if (rows.length === 0) return 'Place at least one well';
  if (rows.some((r) => !r.well_name.trim())) return 'Every well needs an id';
  const names = rows.map((r) => r.well_name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return 'Well ids must be unique';
  if (rows.some((r) => r.t0 == null || r.t1 == null)) {
    return 'Enter start and end years for all wells';
  }
  if (rows.some((r) => (r.t1 as number) < (r.t0 as number))) {
    return 'End year must not be before start year';
  }
  return null;
}

function apiErrorDetail(err: unknown): string {
  if (!err) return 'Request failed';
  if (typeof err === 'string') return err;
  const anyErr = err as {
    message?: string;
    detail?: unknown;
    response?: { data?: { detail?: unknown } };
  };
  const detail = anyErr?.response?.data?.detail ?? anyErr?.detail ?? anyErr?.message;
  if (typeof detail === 'string') return detail;
  if (detail != null) {
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Request failed';
    }
  }
  return 'Request failed';
}

type OpenState = {
  pattern: ProdGridPattern | '';
  spacingM: number | null;
  injectorsOn: boolean;
  production: number | null;
  injection: number | null;
  startYear: number | null;
  maxProdPerYear: number | null;
  maxInjPerYear: number | null;
};

const positiveOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;

/** Saved layer config only — no defaults are filled in. */
function resolveOpenState(initialConfig: MbAutoPlaceConfig | null | undefined): OpenState {
  if (!initialConfig) {
    return {
      pattern: '',
      spacingM: null,
      injectorsOn: false,
      production: null,
      injection: null,
      startYear: null,
      maxProdPerYear: null,
      maxInjPerYear: null,
    };
  }
  const floorOrNull = (v: unknown) => {
    const n = positiveOrNull(v);
    return n == null ? null : Math.floor(n);
  };
  return {
    pattern:
      initialConfig.pattern === 'checkerboard' || initialConfig.pattern === 'regular'
        ? initialConfig.pattern
        : '',
    spacingM: positiveOrNull(initialConfig.spacingM),
    injectorsOn: Boolean(initialConfig.injectorsOn),
    production: floorOrNull(initialConfig.production),
    injection:
      typeof initialConfig.injection === 'number' && initialConfig.injection >= 0
        ? Math.floor(initialConfig.injection)
        : null,
    startYear: floorOrNull(initialConfig.startYear),
    maxProdPerYear: floorOrNull(initialConfig.maxProdPerYear),
    maxInjPerYear: floorOrNull(initialConfig.maxInjPerYear),
  };
}

export const MaterialBalanceAutoPlaceDialog: React.FC<MaterialBalanceAutoPlaceDialogProps> = ({
  open,
  onClose,
  scenarioId,
  layerId,
  layerName,
  contour,
  origin = null,
  initialConfig = null,
  savedWells = EMPTY_SEED_ROWS,
  layerQStartTd = null,
  year0,
  year1 = AUTO_PLACE_DEFAULTS.year1,
  qStartTd = AUTO_PLACE_DEFAULTS.qStartTd,
  hasExistingWells = false,
  applying = false,
  onApply,
}) => {
  const boot = resolveOpenState(initialConfig);

  const [tab, setTab] = useState<AutoPlaceDialogTab>('manual');
  const [pattern, setPattern] = useState<ProdGridPattern | ''>(boot.pattern);
  const [spacingM, setSpacingM] = useState<number | null>(boot.spacingM);
  const [injectorsOn, setInjectorsOn] = useState(boot.injectorsOn);
  const [production, setProduction] = useState<number | null>(boot.production);
  const [injection, setInjection] = useState<number | null>(boot.injection);
  const [startYear, setStartYear] = useState<number | null>(boot.startYear);
  const [maxProdPerYear, setMaxProdPerYear] = useState<number | null>(boot.maxProdPerYear);
  const [maxInjPerYear, setMaxInjPerYear] = useState<number | null>(boot.maxInjPerYear);
  const [viewYear, setViewYear] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gridPreview, setGridPreview] = useState<AutoPlaceResult | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);

  const [aiDescription, setAiDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiNotes, setAiNotes] = useState<string | null>(null);
  const [aiPreview, setAiPreview] = useState<AutoPlaceResult | null>(null);

  const [manualRows, setManualRows] = useState<SeedWellRow[]>([]);
  const [manualSelectedId, setManualSelectedId] = useState<string | null>(null);
  const [seedTool, setSeedTool] = useState<SeedTool | ''>('');
  const [seedRatioKeyValue, setSeedRatioKeyValue] = useState('');
  const [seedDistanceM, setSeedDistanceM] = useState<number | null>(null);

  const existingWells = useMemo<MbWellsAiExistingWell[]>(
    () =>
      savedWells
        .filter((w) => w.x != null && w.y != null)
        .map((w) => ({
          well_name: w.well_name,
          kind: w.kind,
          x: w.x as number,
          y: w.y as number,
          t0: w.t0 ?? undefined,
          t1: w.t1 ?? undefined,
          q_start_td: w.q_start_td,
        })),
    [savedWells]
  );

  // Seed the manual table only when the dialog opens, not on every parent re-render
  const savedWellsRef = useRef(savedWells);
  savedWellsRef.current = savedWells;

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      return;
    }
    setManualRows(savedWellsRef.current.map((w) => ({ ...w })));
    setManualSelectedId(null);
    const next = resolveOpenState(initialConfig);
    setPattern(next.pattern);
    setSpacingM(next.spacingM);
    setInjectorsOn(next.injectorsOn);
    setProduction(next.production);
    setInjection(next.injection);
    setStartYear(next.startYear);
    setMaxProdPerYear(next.maxProdPerYear);
    setMaxInjPerYear(next.maxInjPerYear);
    setViewYear(null);
    setConfirmOpen(false);
    setTab('manual');
    setGridPreview(null);
    setGridError(null);
    setAiError(null);
    setAiLoading(false);
    setAiNotes(null);
    setAiPreview(null);
  }, [open, initialConfig]);

  // Parent rebuilds the contour array every render — key on coordinates instead.
  const contourKey = contour.map((p) => `${p.x},${p.y}`).join(';');

  // Any change of grid inputs invalidates the built grid
  useEffect(() => {
    setGridPreview(null);
    setGridError(null);
  }, [
    contourKey,
    pattern,
    spacingM,
    injectorsOn,
    production,
    injection,
    startYear,
    maxProdPerYear,
    maxInjPerYear,
    year1,
    qStartTd,
  ]);

  const gridParamsReady =
    pattern !== '' &&
    spacingM != null &&
    startYear != null &&
    maxProdPerYear != null &&
    (!injectorsOn || (production != null && injection != null && maxInjPerYear != null));

  const handleBuildGrid = () => {
    if (
      !gridParamsReady ||
      contour.length < 3 ||
      spacingM == null ||
      startYear == null ||
      maxProdPerYear == null
    ) {
      return;
    }
    const est = estimateAutoPlaceGrid(contour, spacingM);
    if (est.tooLarge) {
      setGridPreview(null);
      setGridError(
        `Spacing ${spacingM} m gives ~${est.producers.toLocaleString()} producers on this contour ` +
          `(limit ${MAX_AUTO_PLACE_PRODUCERS.toLocaleString()}). ` +
          `Use spacing ≥ ${est.minSpacingM.toLocaleString()} m or check the contour.`
      );
      return;
    }
    const result = autoPlaceWells(contour, {
      pattern,
      spacingM,
      production: injectorsOn ? (production as number) : 1,
      injection: injectorsOn ? (injection as number) : 0,
      startYear,
      maxProdPerYear,
      maxInjPerYear: injectorsOn ? (maxInjPerYear as number) : 0,
      year1,
      qStartTd,
    });
    setGridError(null);
    setGridPreview(result);
  };

  const preview = tab === 'ai' ? aiPreview : tab === 'grid' ? gridPreview : null;

  // On recalculation jump slider to last online year and show all wells
  useEffect(() => {
    if (!preview) return;
    setViewYear(preview.meta.lastOnlineYear);
  }, [preview]);

  const sliderMin = preview?.meta.startYear ?? 0;
  const sliderMax = Math.max(sliderMin, preview?.meta.lastOnlineYear ?? 0);
  const sliderValue = Math.min(sliderMax, Math.max(sliderMin, viewYear ?? sliderMax));

  const mapWells = useMemo(() => {
    if (!preview) {
      return existingWells.map((w) => ({
        id: w.well_name,
        x: w.x,
        y: w.y,
        kind: w.kind,
        t0: w.t0,
      }));
    }
    return preview.wells
      .filter((w) => viewYear == null || w.t0 <= viewYear)
      .map((w) => ({
        id: w.well_name,
        x: w.x,
        y: w.y,
        kind: w.kind,
        t0: w.t0,
      }));
  }, [preview, viewYear, existingWells]);

  const activeCounts = useMemo(() => {
    const nProd = mapWells.filter((w) => w.kind === 'prod').length;
    const nInj = mapWells.filter((w) => w.kind === 'inj').length;
    return { nProd, nInj, total: nProd + nInj };
  }, [mapWells]);

  const canApply = Boolean(preview && preview.wells.length > 0 && contour.length >= 3);

  const handleApplyClick = () => {
    if (!preview || !canApply) return;
    setConfirmOpen(true);
  };

  const handleConfirmApply = async () => {
    if (!preview || !canApply) return;
    await onApply(preview);
    setConfirmOpen(false);
  };

  const manualMapWells = useMemo(
    () =>
      manualRows
        .filter((r) => r.x != null && r.y != null)
        .map((r) => ({
          id: r.id,
          name: r.well_name,
          kind: r.kind,
          x: r.x as number,
          y: r.y as number,
        })),
    [manualRows]
  );

  const seedRatio = SEED_RATIOS.find((r) => seedRatioKey(r) === seedRatioKeyValue) ?? null;

  const placementAt = useMemo<((pt: MbPoint) => SeedCandidate[]) | null>(() => {
    if (contour.length < 3 || seedTool === '') return null;
    if (seedTool === 'prod' || seedTool === 'inj') {
      const kind = seedTool;
      return (pt) =>
        pointInPoly(pt.x, pt.y, contour) ? [{ kind, x: pt.x, y: pt.y, center: true }] : [];
    }
    if (!seedRatio || seedDistanceM == null) return null;
    const dKm = seedDistanceM / 1000;
    const taken = manualMapWells.map((w) => ({ x: w.x, y: w.y }));
    return (pt) =>
      pointInPoly(pt.x, pt.y, contour)
        ? carveSeedCandidates(buildSeedPattern(pt, seedRatio, dKm), contour, taken, dKm * 0.5)
        : [];
  }, [contour, seedTool, seedRatio, seedDistanceM, manualMapWells]);

  const handleManualPlace = useCallback(
    (candidates: SeedCandidate[]) => {
      const names = manualRows.map((r) => r.well_name);
      const prodNames = nextSeedWellNames(
        names,
        'prod',
        candidates.filter((c) => c.kind === 'prod').length
      );
      const injNames = nextSeedWellNames(
        names,
        'inj',
        candidates.filter((c) => c.kind === 'inj').length
      );
      const stamp = Date.now();
      const added: SeedWellRow[] = candidates.map((c, i) => ({
        id: `seed-${stamp}-${i}`,
        well_name: (c.kind === 'prod' ? prodNames : injNames).shift() as string,
        kind: c.kind,
        t0: null,
        t1: null,
        x: round4(c.x),
        y: round4(c.y),
        q_start_td: c.kind === 'prod' ? layerQStartTd : null,
      }));
      const centerIdx = candidates.findIndex((c) => c.center);
      setManualRows([...manualRows, ...added]);
      setManualSelectedId(added[centerIdx >= 0 ? centerIdx : 0]?.id ?? null);
    },
    [manualRows, layerQStartTd]
  );

  const handleManualMove = useCallback((id: string, pt: MbPoint) => {
    setManualRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, x: round4(pt.x), y: round4(pt.y) } : r))
    );
  }, []);

  const manualError = validateManualRows(manualRows);

  const handleManualSave = async () => {
    if (manualError) return;
    const wells: AutoPlaceWell[] = manualRows.map((r) => ({
      well_name: r.well_name.trim(),
      kind: r.kind,
      // Saved wells without coordinates keep them empty
      x: r.x as number,
      y: r.y as number,
      t0: r.t0 as number,
      t1: r.t1 as number,
      q_start_td: r.kind === 'prod' ? r.q_start_td : null,
    }));
    const nProd = wells.filter((w) => w.kind === 'prod').length;
    const nInj = wells.length - nProd;
    const firstYear = Math.min(...wells.map((w) => w.t0));
    const lastYear = Math.max(...wells.map((w) => w.t1));
    await onApply({
      wells,
      injField: buildDefaultInjField(
        firstYear,
        lastYear,
        nProd,
        layerQStartTd ?? qStartTd,
        nInj > 0
      ),
      applyMode: 'delta',
      source: 'manual',
      meta: {
        areaKm2: Math.round(contourAreaKm2(contour) * 1000) / 1000,
        nProd,
        nInj,
        spacingKm: (seedDistanceM ?? 0) / 1000,
        spacingM: seedDistanceM ?? 0,
        pattern: 'regular',
        production: seedRatio?.prod ?? 1,
        injection: seedRatio?.inj ?? (nInj > 0 ? 1 : 0),
        startYear: firstYear,
        maxProdPerYear: nProd,
        maxInjPerYear: nInj,
        lastOnlineYear: Math.max(...wells.map((w) => w.t0)),
      },
    });
  };

  const aiYear0 = Math.floor(year0 ?? startYear ?? new Date().getFullYear());

  const handleAiGenerate = async () => {
    if (aiLoading || !aiDescription.trim() || contour.length < 3) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await scenariosApi.suggestMaterialBalanceWellsAi(scenarioId, layerId, {
        description: aiDescription.trim(),
        contour: contour.map((p) => ({ x: p.x, y: p.y })),
        existing_wells: existingWells,
        year0: aiYear0,
        year1: Math.floor(year1),
        q_start_td: qStartTd,
      });
      const applyMode = res.apply_mode === 'delta' ? 'delta' : 'replace';
      const existingAsAuto = existingWells.map((w) => ({
        well_name: w.well_name,
        kind: w.kind,
        x: w.x,
        y: w.y,
        t0: w.t0 ?? aiYear0,
        t1: w.t1 ?? Math.floor(year1),
        q_start_td: w.kind === 'prod' ? (w.q_start_td ?? null) : null,
      }));
      const result = finalizeAutoPlaceFromAiWells(res.wells || [], contour, {
        year1,
        qStartTd,
        applyMode,
        existingWells: existingAsAuto,
        removeWellNames: res.remove_well_names || [],
      });
      if (!result.wells.length) {
        setAiError('No usable wells in the suggested plan');
        setAiPreview(null);
        return;
      }
      setAiPreview(result);
      setAiNotes(res.notes ?? null);
    } catch (err) {
      setAiError(apiErrorDetail(err));
    } finally {
      setAiLoading(false);
    }
  };

  const sectionBoxSx = {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1.5,
    p: 2,
    bgcolor: 'action.hover',
  } as const;

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 1.75 }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <AutoAwesomeIcon fontSize='small' color='primary' />
          <Typography variant='subtitle1' fontWeight={600}>
            Seed · {layerName}
          </Typography>
        </Stack>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
          {preview
            ? `${preview.meta.nProd} prod${preview.meta.nInj ? ` · ${preview.meta.nInj} inj` : ''} · online ${preview.meta.startYear}–${preview.meta.lastOnlineYear}`
            : 'Place wells and schedule online years'}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          px: 3,
          pt: 3,
          pb: 2.5,
        }}
      >
        {contour.length < 3 ? (
          <Typography color='text.secondary'>
            Save a contour (3+ points) for this layer before auto-placing wells.
          </Typography>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md:
                  tab === 'manual'
                    ? 'minmax(0, 1fr) minmax(420px, 1fr)'
                    : 'minmax(0, 1.35fr) minmax(280px, 0.9fr)',
              },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <Stack spacing={1.5} sx={{ minHeight: 0, minWidth: 0, height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {tab === 'manual' ? (
                  <SeedManualMap
                    contour={contour}
                    origin={origin}
                    wells={manualMapWells}
                    selectedId={manualSelectedId}
                    onSelect={setManualSelectedId}
                    placementAt={placementAt}
                    onPlace={handleManualPlace}
                    onMove={handleManualMove}
                    disabled={applying}
                    height='100%'
                  />
                ) : (
                  <VoronoiMbMap
                    contour={contour}
                    wells={mapWells}
                    grid={null}
                    origin={origin}
                    fitKey={
                      preview
                        ? `${tab}|${preview.meta.spacingM}|${preview.meta.pattern}|${preview.meta.nProd}|${preview.meta.nInj}|${preview.meta.lastOnlineYear}|${contour.length}`
                        : `${tab}|empty`
                    }
                    height='100%'
                  />
                )}
              </Box>
              {tab === 'manual' ? null : (
                <Box
                  sx={{
                    px: 1.5,
                    pt: 1,
                    pb: 0.75,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='baseline'
                    justifyContent='space-between'
                    spacing={1}
                    sx={{ mb: 0.25, minWidth: 0 }}
                  >
                    <Typography variant='caption' color='text.secondary' fontWeight={600} noWrap>
                      {preview ? `Year ${sliderValue}` : 'Saved wells'}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      noWrap
                      sx={{ minWidth: 0, textAlign: 'right' }}
                    >
                      {preview ? 'Active' : 'Total'} {activeCounts.total}
                      {activeCounts.nProd || activeCounts.nInj
                        ? ` · ${activeCounts.nProd} prod · ${activeCounts.nInj} inj`
                        : ''}
                    </Typography>
                  </Stack>
                  {preview ? (
                    <Box sx={{ px: 1.25, pt: 0.5, pb: 2.25 }}>
                      <Slider
                        size='small'
                        min={sliderMin}
                        max={sliderMax}
                        step={1}
                        value={sliderValue}
                        onChange={(_, v) => setViewYear(v as number)}
                        valueLabelDisplay='auto'
                        marks={
                          sliderMax > sliderMin
                            ? [
                                { value: sliderMin, label: String(sliderMin) },
                                { value: sliderMax, label: String(sliderMax) },
                              ]
                            : [{ value: sliderMin, label: String(sliderMin) }]
                        }
                        sx={{
                          width: '100%',
                          '& .MuiSlider-markLabel': {
                            fontSize: 11,
                            color: 'text.secondary',
                          },
                          '& .MuiSlider-markLabel[data-index="0"]': {
                            transform: 'translateX(0%)',
                          },
                          '& .MuiSlider-markLabel[data-index="1"]': {
                            transform: 'translateX(-100%)',
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', pb: 0.5 }}
                    >
                      {existingWells.length
                        ? 'Saved wells with coordinates. Build a grid or create with AI to preview changes.'
                        : 'No saved wells. Build a grid or create with AI to preview wells.'}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>

            <Stack
              spacing={1.5}
              sx={{
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                py: 0.5,
                pr: 0.5,
                display: 'flex',
              }}
            >
              <Tabs
                value={tab}
                onChange={(_, v: AutoPlaceDialogTab) => setTab(v)}
                variant='fullWidth'
                sx={{
                  minHeight: 36,
                  flexShrink: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13 },
                }}
              >
                <Tab value='manual' label='Manual' />
                <Tab value='grid' label='Grid settings' />
                <Tab value='ai' label='Create with AI' />
              </Tabs>

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {tab === 'manual' ? (
                  <SeedManualPanel
                    rows={manualRows}
                    onRowsChange={setManualRows}
                    selectedId={manualSelectedId}
                    onSelect={setManualSelectedId}
                    tool={seedTool}
                    onToolChange={setSeedTool}
                    ratioKey={seedRatioKeyValue}
                    onRatioKeyChange={setSeedRatioKeyValue}
                    distanceM={seedDistanceM}
                    onDistanceMChange={setSeedDistanceM}
                    disabled={applying}
                  />
                ) : tab === 'grid' ? (
                  <Stack spacing={2.25}>
                    <Box sx={sectionBoxSx}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{ mb: 2 }}
                      >
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#3dcc7a',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant='subtitle2' fontWeight={600}>
                            Producers
                          </Typography>
                        </Stack>
                        {gridPreview && (
                          <Typography variant='caption' color='text.secondary' fontWeight={600}>
                            {gridPreview.meta.nProd}
                          </Typography>
                        )}
                      </Stack>
                      <Stack spacing={2}>
                        <FormControl size='small' fullWidth>
                          <InputLabel>Grid</InputLabel>
                          <Select
                            label='Grid'
                            value={pattern}
                            onChange={(e) => setPattern(e.target.value as ProdGridPattern | '')}
                          >
                            <MenuItem value='regular'>Regular</MenuItem>
                            <MenuItem value='checkerboard'>Checkerboard</MenuItem>
                          </Select>
                        </FormControl>
                        <CommitNumberField
                          size='small'
                          label='Spacing (m)'
                          value={spacingM}
                          allowEmpty={false}
                          formatCommitted={(n) => Math.round(Math.max(n, 0.001) * 1000) / 1000}
                          onCommit={(n) => {
                            if (n == null || n <= 0) return;
                            setSpacingM(Math.round(n * 1000) / 1000);
                          }}
                          fullWidth
                        />
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        ...sectionBoxSx,
                        opacity: injectorsOn ? 1 : 0.65,
                      }}
                    >
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{ mb: injectorsOn ? 2 : 0 }}
                      >
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#4aa3ff',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant='subtitle2' fontWeight={600}>
                            Injectors
                          </Typography>
                          {gridPreview && injectorsOn && (
                            <Typography variant='caption' color='text.secondary' fontWeight={600}>
                              {gridPreview.meta.nInj}
                            </Typography>
                          )}
                        </Stack>
                        <Switch
                          checked={injectorsOn}
                          onChange={(_, v) => setInjectorsOn(v)}
                          size='small'
                        />
                      </Stack>
                      {injectorsOn && (
                        <Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mb: 1 }}
                          >
                            Ratio
                          </Typography>
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <CommitNumberField
                              size='small'
                              label='Prod'
                              value={production}
                              allowEmpty={false}
                              formatCommitted={(n) => Math.max(1, Math.floor(n))}
                              onCommit={(n) => {
                                if (n == null) return;
                                setProduction(Math.max(1, Math.floor(n)));
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                            <Typography variant='body2' color='text.secondary' sx={{ px: 0.25 }}>
                              :
                            </Typography>
                            <CommitNumberField
                              size='small'
                              label='Inj'
                              value={injection}
                              allowEmpty={false}
                              formatCommitted={(n) => Math.max(0, Math.floor(n))}
                              onCommit={(n) => {
                                if (n == null) return;
                                setInjection(Math.max(0, Math.floor(n)));
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                          </Stack>
                        </Box>
                      )}
                    </Box>

                    <Box sx={sectionBoxSx}>
                      <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 2 }}>
                        Schedule
                      </Typography>
                      <Stack spacing={2}>
                        <CommitNumberField
                          size='small'
                          label='Prod/Inj start'
                          value={startYear}
                          allowEmpty={false}
                          formatCommitted={(n) => Math.floor(n)}
                          onCommit={(n) => {
                            if (n == null) return;
                            setStartYear(Math.floor(n));
                          }}
                          fullWidth
                        />
                        <Stack direction='row' spacing={1.5}>
                          <CommitNumberField
                            size='small'
                            label='Max prod / yr'
                            value={maxProdPerYear}
                            allowEmpty={false}
                            formatCommitted={(n) => Math.max(1, Math.floor(n))}
                            onCommit={(n) => {
                              if (n == null) return;
                              setMaxProdPerYear(Math.max(1, Math.floor(n)));
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          <CommitNumberField
                            size='small'
                            label='Max inj / yr'
                            value={maxInjPerYear}
                            allowEmpty={false}
                            disabled={!injectorsOn}
                            formatCommitted={(n) => Math.max(1, Math.floor(n))}
                            onCommit={(n) => {
                              if (n == null) return;
                              setMaxInjPerYear(Math.max(1, Math.floor(n)));
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                        </Stack>
                      </Stack>
                    </Box>

                    {gridError ? <Alert severity='error'>{gridError}</Alert> : null}
                    {gridPreview && gridPreview.wells.length === 0 && (
                      <Typography variant='caption' color='error'>
                        No wells fit — try smaller spacing or check the contour.
                      </Typography>
                    )}
                    <Button
                      variant='outlined'
                      disabled={!gridParamsReady || applying || contour.length < 3}
                      onClick={handleBuildGrid}
                      startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    >
                      {gridPreview ? 'Rebuild grid' : 'Build grid'}
                    </Button>
                    {!gridParamsReady ? (
                      <Typography variant='caption' color='text.secondary'>
                        Fill in grid, spacing and schedule to build a preview.
                      </Typography>
                    ) : null}
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant='body2' color='text.secondary'>
                      Describe the plan and constraints (counts, spacing, keep/move existing wells,
                      schedule). Contour coordinates and current wells are included automatically.
                      Full redesign replaces the grid; small edits keep untouched wells.
                    </Typography>
                    <TextField
                      label='Wishes & constraints'
                      placeholder={
                        'e.g. Keep existing producers, add 3 injectors on the flanks, ' +
                        'no more than 2 new wells per year from 2027, prefer ~250 m spacing, ' +
                        'avoid the northern thin area…'
                      }
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      multiline
                      minRows={8}
                      fullWidth
                      disabled={aiLoading || applying}
                    />
                    <Typography variant='caption' color='text.secondary'>
                      Contour {contour.length} pts
                      {existingWells.length
                        ? ` · ${existingWells.length} existing well(s) included`
                        : ' · no existing wells'}
                      {' · '}
                      years {aiYear0}–{Math.floor(year1)}
                    </Typography>
                    {aiError ? <Alert severity='error'>{aiError}</Alert> : null}
                    {aiNotes ? (
                      <Alert severity='info' sx={{ py: 0.5 }}>
                        {aiNotes}
                        {aiPreview?.applyMode ? (
                          <Typography
                            component='span'
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Apply mode:{' '}
                            {aiPreview.applyMode === 'delta'
                              ? 'delta (keep untouched wells)'
                              : 'replace all'}
                            {` · ${aiPreview.meta.nProd} prod · ${aiPreview.meta.nInj} inj`}
                          </Typography>
                        ) : null}
                      </Alert>
                    ) : null}
                    {!aiPreview && !aiLoading && !aiError ? (
                      <Typography variant='caption' color='text.secondary'>
                        Run Generate to preview the well grid on the map, then Apply to save.
                      </Typography>
                    ) : null}
                    {aiPreview && aiPreview.wells.length === 0 ? (
                      <Typography variant='caption' color='error'>
                        No wells in the plan — adjust the description and regenerate.
                      </Typography>
                    ) : null}
                    <Button
                      variant='outlined'
                      disabled={
                        aiLoading || applying || !aiDescription.trim() || contour.length < 3
                      }
                      onClick={() => void handleAiGenerate()}
                      startIcon={
                        aiLoading ? (
                          <CircularProgress size={14} color='inherit' />
                        ) : (
                          <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                        )
                      }
                    >
                      {aiLoading ? 'Generating…' : aiPreview ? 'Regenerate' : 'Generate'}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.75 }}>
        {tab === 'manual' && manualError ? (
          <Typography variant='caption' color='text.secondary' sx={{ mr: 'auto', pl: 0.5 }}>
            {manualError}
          </Typography>
        ) : null}
        <Button onClick={onClose} disabled={applying || aiLoading}>
          Cancel
        </Button>
        {tab === 'manual' ? (
          <Button
            variant='contained'
            disabled={Boolean(manualError) || applying}
            onClick={() => void handleManualSave()}
          >
            {applying ? 'Saving…' : 'Save wells'}
          </Button>
        ) : (
          <Button
            variant='contained'
            disabled={!canApply || applying || aiLoading}
            onClick={handleApplyClick}
            startIcon={<AutoAwesomeIcon />}
          >
            {applying ? 'Applying…' : 'Apply'}
          </Button>
        )}
      </DialogActions>

      <Dialog
        open={confirmOpen}
        onClose={applying ? undefined : () => setConfirmOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {preview?.applyMode === 'delta' ? 'Apply well changes?' : 'Replace layer wells?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            {preview?.applyMode === 'delta' ? (
              <>
                Only changed wells on layer{' '}
                <Typography component='span' variant='body2' fontWeight={600} color='text.primary'>
                  {layerName}
                </Typography>{' '}
                will be created, updated, or removed. Untouched wells stay as they are.
              </>
            ) : (
              <>
                This configuration will fully rewrite all wells on layer{' '}
                <Typography component='span' variant='body2' fontWeight={600} color='text.primary'>
                  {layerName}
                </Typography>
                .
                {hasExistingWells
                  ? ' Existing wells will be deleted and replaced.'
                  : ' Wells will be created on this layer.'}
              </>
            )}
          </Typography>
          {preview && (
            <Typography variant='body2' sx={{ mt: 1.5 }}>
              {preview.meta.nProd} producers
              {preview.meta.nInj ? ` · ${preview.meta.nInj} injectors` : ''} · online{' '}
              {preview.meta.startYear}–{preview.meta.lastOnlineYear}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>
            Back
          </Button>
          <Button
            variant='contained'
            color={preview?.applyMode === 'delta' ? 'primary' : 'warning'}
            disabled={applying}
            onClick={() => void handleConfirmApply()}
            startIcon={<AutoAwesomeIcon />}
          >
            {applying
              ? 'Applying…'
              : preview?.applyMode === 'delta'
                ? 'Apply changes'
                : 'Replace wells'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
